import React, { useEffect, useState } from "react";
import api from "../../lib/api";
import { toast } from "sonner";
import { Plus, Pencil, Check, X, Trash2, ChevronDown, ChevronRight } from "lucide-react";

export default function AdminTopics() {
  const [topics, setTopics] = useState([]);
  const [subs, setSubs] = useState([]);
  const [newTopic, setNewTopic] = useState({ name: "", slug: "", is_free: false });
  const [newSubByTopic, setNewSubByTopic] = useState({}); // topic_id -> {name, slug}
  const [editingTopic, setEditingTopic] = useState(null);
  const [topicForm, setTopicForm] = useState({});
  const [editingSub, setEditingSub] = useState(null);
  const [subForm, setSubForm] = useState({});
  const [expanded, setExpanded] = useState({}); // topic_id -> bool
  const [refsByTopic, setRefsByTopic] = useState({}); // topic_id -> {count, refs} | "loading"

  // Bağlı soru numaraları (manuel kontrol) — salt-okunur listeleme
  const toggleRefs = async (t) => {
    if (refsByTopic[t.topic_id]) {
      setRefsByTopic((m) => { const n = { ...m }; delete n[t.topic_id]; return n; });
      return;
    }
    setRefsByTopic((m) => ({ ...m, [t.topic_id]: "loading" }));
    try {
      const { data } = await api.get(`/catalog/topics/${t.topic_id}/question-refs`);
      setRefsByTopic((m) => ({ ...m, [t.topic_id]: data }));
    } catch {
      setRefsByTopic((m) => { const n = { ...m }; delete n[t.topic_id]; return n; });
      toast.error("Soru numaraları alınamadı");
    }
  };

  const load = async () => {
    const [t, s] = await Promise.all([api.get("/catalog/topics"), api.get("/catalog/subtopics")]);
    setTopics(t.data);
    setSubs(s.data);
  };
  useEffect(() => { load(); }, []);

  const addTopic = async () => {
    if (!newTopic.name || !newTopic.slug) return;
    try {
      await api.post("/catalog/topics", { ...newTopic, order: topics.length });
      toast.success("Konu eklendi");
      setNewTopic({ name: "", slug: "", is_free: false });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eklenemedi"); }
  };

  const startEditTopic = (t) => {
    setEditingTopic(t.topic_id);
    setTopicForm({
      name: t.name || "", slug: t.slug || "",
      is_free: !!t.is_free, is_active: t.is_active !== false,
      description: t.description || "",
    });
  };
  const saveTopic = async (topic_id) => {
    if (!topicForm.name?.trim() || !topicForm.slug?.trim()) { toast.error("Ad ve slug zorunlu"); return; }
    try {
      await api.put(`/catalog/topics/${topic_id}`, topicForm);
      toast.success("Konu güncellendi");
      setEditingTopic(null); setTopicForm({});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Güncellenemedi"); }
  };
  const toggleTopicActive = async (t) => {
    try {
      await api.put(`/catalog/topics/${t.topic_id}`, { is_active: !(t.is_active !== false) });
      toast.success(t.is_active !== false ? "Konu pasifleştirildi" : "Konu aktifleştirildi");
      load();
    } catch { toast.error("Güncellenemedi"); }
  };
  const [confirmHard, setConfirmHard] = useState(null); // {topic, usage}
  const deleteTopic = async (t) => {
    const active = t.is_active !== false;
    if (active && !window.confirm(`"${t.name}" konusunu silmek istediğinize emin misiniz?`)) return;
    try {
      const { data } = await api.delete(`/catalog/topics/${t.topic_id}`);
      toast.success(data.message || (data.deleted ? "Silindi" : "Pasifleştirildi"));
      load();
    } catch (e) {
      const detail = e?.response?.data?.detail;
      if (e?.response?.status === 409 && detail?.requires_confirmation) {
        setConfirmHard({ topic: t, usage: detail.usage || {} });
      } else {
        toast.error((typeof detail === "string" && detail) || "Silinemedi");
      }
    }
  };
  const hardDeleteConfirmed = async () => {
    if (!confirmHard) return;
    try {
      const { data } = await api.delete(`/catalog/topics/${confirmHard.topic.topic_id}?confirm=true`);
      toast.success(data.message || "Konu kalıcı olarak silindi");
      setConfirmHard(null);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  const addSub = async (topic_id) => {
    const draft = newSubByTopic[topic_id] || {};
    if (!draft.name || !draft.slug) { toast.error("Alt konu ad ve slug gerekli"); return; }
    try {
      await api.post("/catalog/subtopics", {
        topic_id, name: draft.name, slug: draft.slug,
        order: subs.filter((x) => x.topic_id === topic_id).length,
        is_active: draft.is_active !== false,
      });
      toast.success("Alt konu eklendi");
      setNewSubByTopic((m) => ({ ...m, [topic_id]: { name: "", slug: "", is_active: true } }));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Eklenemedi"); }
  };
  const startEditSub = (s) => {
    setEditingSub(s.subtopic_id);
    setSubForm({ name: s.name || "", slug: s.slug || "", is_active: s.is_active !== false });
  };
  const saveSub = async (subtopic_id) => {
    if (!subForm.name?.trim() || !subForm.slug?.trim()) { toast.error("Ad ve slug zorunlu"); return; }
    try {
      await api.put(`/catalog/subtopics/${subtopic_id}`, subForm);
      toast.success("Alt konu güncellendi");
      setEditingSub(null); setSubForm({});
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Güncellenemedi"); }
  };
  const toggleSubActive = async (s) => {
    try {
      await api.put(`/catalog/subtopics/${s.subtopic_id}`, { is_active: !(s.is_active !== false) });
      load();
    } catch { toast.error("Güncellenemedi"); }
  };
  const deleteSub = async (s) => {
    if (!window.confirm(`"${s.name}" alt konusunu silmek istediğinize emin misiniz?`)) return;
    try {
      const { data } = await api.delete(`/catalog/subtopics/${s.subtopic_id}`);
      toast.success(data.message || (data.deleted ? "Silindi" : "Pasifleştirildi"));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Silinemedi"); }
  };

  const setNewSub = (topic_id, patch) =>
    setNewSubByTopic((m) => ({ ...m, [topic_id]: { ...(m[topic_id] || { name: "", slug: "", is_active: true }), ...patch } }));

  return (
    <div className="space-y-6">
      {confirmHard && (
        <div data-testid="admin-topic-hard-delete-modal"
             className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4">
          <div className="w-full max-w-md bg-[#171717] border border-rose-500/40 rounded-xl p-6">
            <div className="font-display text-lg text-rose-300 mb-2">⚠️ Kalıcı Silme</div>
            <p className="text-sm text-slate-300 mb-3">
              Bu konuyla ilişkili öğrenci geçmişi bulunuyor. Konuyu kalıcı olarak silerseniz
              bu konuya bağlı öğrenci çözüm geçmişi, istatistikler ve ilişkili kayıtlar da
              silinecek. <b>Bu işlem geri alınamaz.</b>
            </p>
            <div className="text-xs text-slate-400 font-mono mb-4 space-y-1">
              <div>Konu: {confirmHard.topic.name}</div>
              <div>Soru: {confirmHard.usage.questions ?? 0} · Alt konu: {confirmHard.usage.subtopics ?? 0}</div>
              <div>Öğrenci yanıtı: {confirmHard.usage.attempts ?? 0} · Oturum: {confirmHard.usage.sessions ?? 0}</div>
            </div>
            <div className="flex gap-2 justify-end">
              <button onClick={() => setConfirmHard(null)} data-testid="admin-topic-hard-delete-cancel"
                      className="px-4 py-2 rounded-lg border border-[#262626] text-sm">
                İptal
              </button>
              <button onClick={hardDeleteConfirmed} data-testid="admin-topic-hard-delete-confirm"
                      className="px-4 py-2 rounded-lg bg-rose-600 hover:bg-rose-500 text-white text-sm font-medium">
                Kalıcı Olarak Sil
              </button>
            </div>
          </div>
        </div>
      )}
      <h1 className="font-display text-3xl tracking-tight">Konular & Alt Konular</h1>

      <section className="bg-[#171717] border border-[#262626] rounded-lg p-4">
        <div className="text-sm font-medium mb-3">Yeni konu</div>
        <div className="grid sm:grid-cols-4 gap-2">
          <input data-testid="admin-topic-new-name" placeholder="Ad" value={newTopic.name}
            onChange={(e) => setNewTopic({ ...newTopic, name: e.target.value })}
            className="px-3 py-2 rounded-md bg-[#0A0A0A] border border-[#262626]"/>
          <input data-testid="admin-topic-new-slug" placeholder="slug" value={newTopic.slug}
            onChange={(e) => setNewTopic({ ...newTopic, slug: e.target.value })}
            className="px-3 py-2 rounded-md bg-[#0A0A0A] border border-[#262626]"/>
          <label className="inline-flex items-center gap-2 text-sm text-slate-300">
            <input type="checkbox" checked={newTopic.is_free}
              onChange={(e) => setNewTopic({ ...newTopic, is_free: e.target.checked })}/>
            Ücretsiz konu
          </label>
          <button data-testid="admin-topic-add-btn" onClick={addTopic}
            className="px-4 py-2 rounded-md bg-[#EA580C] hover:bg-[#F97316] inline-flex items-center gap-2">
            <Plus size={16}/> Ekle
          </button>
        </div>
      </section>

      <div className="space-y-3">
        {topics.map((t) => {
          const active = t.is_active !== false;
          const topicSubs = subs.filter((s) => s.topic_id === t.topic_id);
          const isEdit = editingTopic === t.topic_id;
          const isOpen = !!expanded[t.topic_id];
          return (
            <div key={t.topic_id} className="rounded-lg border border-[#262626] bg-[#0F0F0F]">
              {/* header */}
              <div className="flex items-center gap-3 p-3 border-b border-[#262626] bg-[#171717]">
                <button onClick={() => setExpanded((m) => ({ ...m, [t.topic_id]: !isOpen }))}
                  className="p-1 rounded hover:bg-[#0A0A0A]" data-testid={`admin-topic-expand-${t.topic_id}`}>
                  {isOpen ? <ChevronDown size={16}/> : <ChevronRight size={16}/>}
                </button>
                <div className="flex-1 min-w-0">
                  {isEdit ? (
                    <div className="grid sm:grid-cols-3 gap-2">
                      <input data-testid={`admin-topic-edit-name-${t.topic_id}`}
                        value={topicForm.name} onChange={(e) => setTopicForm({ ...topicForm, name: e.target.value })}
                        className="px-2 py-1.5 rounded bg-[#0A0A0A] border border-[#262626]"/>
                      <input data-testid={`admin-topic-edit-slug-${t.topic_id}`}
                        value={topicForm.slug} onChange={(e) => setTopicForm({ ...topicForm, slug: e.target.value })}
                        className="px-2 py-1.5 rounded bg-[#0A0A0A] border border-[#262626] font-mono"/>
                      <input data-testid={`admin-topic-edit-desc-${t.topic_id}`}
                        placeholder="Açıklama" value={topicForm.description || ""}
                        onChange={(e) => setTopicForm({ ...topicForm, description: e.target.value })}
                        className="px-2 py-1.5 rounded bg-[#0A0A0A] border border-[#262626]"/>
                      <label className="text-xs inline-flex items-center gap-1">
                        <input type="checkbox" checked={!!topicForm.is_free}
                          onChange={(e) => setTopicForm({ ...topicForm, is_free: e.target.checked })}/>
                        Ücretsiz
                      </label>
                      <label className="text-xs inline-flex items-center gap-1">
                        <input type="checkbox" checked={topicForm.is_active !== false}
                          data-testid={`admin-topic-edit-active-${t.topic_id}`}
                          onChange={(e) => setTopicForm({ ...topicForm, is_active: e.target.checked })}/>
                        Aktif
                      </label>
                    </div>
                  ) : (
                    <div>
                      <div className="font-medium">{t.name}</div>
                      <div className="text-xs text-slate-500 font-mono">{t.slug} · {topicSubs.length} alt konu</div>
                      {t.description && <div className="text-xs text-slate-500 mt-0.5">{t.description}</div>}
                    </div>
                  )}
                </div>
                <span data-testid={`admin-topic-status-${t.topic_id}`}
                  className={`text-xs px-2 py-0.5 rounded-full ${active ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700 text-slate-300"}`}>
                  {active ? "Aktif" : "Pasif"}
                </span>
                <div className="flex items-center gap-1">
                  {isEdit ? (
                    <>
                      <button onClick={() => saveTopic(t.topic_id)} data-testid={`admin-topic-save-${t.topic_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#EA580C] hover:bg-[#F97316] text-white text-xs">
                        <Check size={12}/> Kaydet
                      </button>
                      <button onClick={() => { setEditingTopic(null); setTopicForm({}); }}
                        data-testid={`admin-topic-cancel-${t.topic_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#262626] text-xs">
                        <X size={12}/> Vazgeç
                      </button>
                    </>
                  ) : (
                    <>
                      <button onClick={() => toggleRefs(t)} data-testid={`admin-topic-refs-toggle-${t.topic_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#262626] hover:border-[#EA580C] text-xs">
                        Bağlı Soru Numaralarını Göster
                      </button>
                      <button onClick={() => startEditTopic(t)} data-testid={`admin-topic-edit-${t.topic_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#262626] hover:border-[#EA580C] text-xs">
                        <Pencil size={12}/> Düzenle
                      </button>
                      <button onClick={() => toggleTopicActive(t)} data-testid={`admin-topic-toggle-${t.topic_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#262626] hover:border-[#EA580C] text-xs">
                        {active ? "Pasifleştir" : "Aktifleştir"}
                      </button>
                      <button onClick={() => deleteTopic(t)} data-testid={`admin-topic-delete-${t.topic_id}`}
                        className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-xs">
                        <Trash2 size={12}/> Sil
                      </button>
                    </>
                  )}
                </div>
              </div>

              {/* bağlı soru numaraları paneli (salt-okunur) */}
              {refsByTopic[t.topic_id] && (
                <div data-testid={`admin-topic-refs-panel-${t.topic_id}`}
                     className="px-3 py-2 border-b border-[#262626] bg-[#0A0A0A]">
                  {refsByTopic[t.topic_id] === "loading" ? (
                    <div className="text-xs text-slate-500">Yükleniyor…</div>
                  ) : (
                    <>
                      <div className="text-xs text-slate-400 mb-1" data-testid={`admin-topic-refs-count-${t.topic_id}`}>
                        Bağlı soru sayısı: {refsByTopic[t.topic_id].count}
                      </div>
                      <div className="flex flex-wrap gap-1">
                        {refsByTopic[t.topic_id].refs.map((r) => (
                          <span key={r}
                                className="text-xs font-mono px-1.5 py-0.5 rounded bg-[#171717] border border-[#262626]">{r}</span>
                        ))}
                      </div>
                    </>
                  )}
                </div>
              )}

              {/* subtopics panel */}
              {isOpen && (
                <div className="p-3 space-y-3">
                  {topicSubs.length === 0 && (
                    <div className="text-xs text-slate-500 border border-dashed border-[#262626] rounded-md p-3">
                      Bu konuya bağlı alt konu yok.
                    </div>
                  )}
                  <ul className="space-y-2">
                    {topicSubs.map((s) => {
                      const sActive = s.is_active !== false;
                      const sEdit = editingSub === s.subtopic_id;
                      return (
                        <li key={s.subtopic_id}
                            className="flex items-center gap-2 p-2 rounded-md border border-[#262626] bg-[#0A0A0A]">
                          {sEdit ? (
                            <div className="flex-1 grid sm:grid-cols-3 gap-2">
                              <input data-testid={`admin-subtopic-edit-name-${s.subtopic_id}`}
                                value={subForm.name} onChange={(e) => setSubForm({ ...subForm, name: e.target.value })}
                                className="px-2 py-1 rounded bg-[#171717] border border-[#262626] text-sm"/>
                              <input data-testid={`admin-subtopic-edit-slug-${s.subtopic_id}`}
                                value={subForm.slug} onChange={(e) => setSubForm({ ...subForm, slug: e.target.value })}
                                className="px-2 py-1 rounded bg-[#171717] border border-[#262626] font-mono text-sm"/>
                              <label className="text-xs inline-flex items-center gap-1">
                                <input type="checkbox" checked={subForm.is_active !== false}
                                  data-testid={`admin-subtopic-edit-active-${s.subtopic_id}`}
                                  onChange={(e) => setSubForm({ ...subForm, is_active: e.target.checked })}/>
                                Aktif
                              </label>
                            </div>
                          ) : (
                            <div className="flex-1 min-w-0">
                              <div className="text-sm">{s.name}</div>
                              <div className="text-xs text-slate-500 font-mono">{s.slug}</div>
                            </div>
                          )}
                          <span data-testid={`admin-subtopic-status-${s.subtopic_id}`}
                            className={`text-xs px-2 py-0.5 rounded-full ${sActive ? "bg-emerald-500/15 text-emerald-300" : "bg-slate-700 text-slate-300"}`}>
                            {sActive ? "Aktif" : "Pasif"}
                          </span>
                          <div className="flex items-center gap-1">
                            {sEdit ? (
                              <>
                                <button onClick={() => saveSub(s.subtopic_id)} data-testid={`admin-subtopic-save-${s.subtopic_id}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded bg-[#EA580C] hover:bg-[#F97316] text-white text-xs">
                                  <Check size={12}/> Kaydet
                                </button>
                                <button onClick={() => { setEditingSub(null); setSubForm({}); }}
                                  data-testid={`admin-subtopic-cancel-${s.subtopic_id}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#262626] text-xs">
                                  <X size={12}/> Vazgeç
                                </button>
                              </>
                            ) : (
                              <>
                                <button onClick={() => startEditSub(s)} data-testid={`admin-subtopic-edit-${s.subtopic_id}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#262626] hover:border-[#EA580C] text-xs">
                                  <Pencil size={12}/> Düzenle
                                </button>
                                <button onClick={() => toggleSubActive(s)} data-testid={`admin-subtopic-toggle-${s.subtopic_id}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-[#262626] hover:border-[#EA580C] text-xs">
                                  {sActive ? "Pasifleştir" : "Aktifleştir"}
                                </button>
                                <button onClick={() => deleteSub(s)} data-testid={`admin-subtopic-delete-${s.subtopic_id}`}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded border border-rose-500/40 text-rose-300 hover:bg-rose-500/10 text-xs">
                                  <Trash2 size={12}/> Sil
                                </button>
                              </>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ul>

                  {/* New subtopic under this topic */}
                  <div className="grid sm:grid-cols-4 gap-2 pt-2 border-t border-dashed border-[#262626]">
                    <input placeholder="Yeni alt konu adı"
                      data-testid={`admin-subtopic-new-name-${t.topic_id}`}
                      value={(newSubByTopic[t.topic_id]?.name) || ""}
                      onChange={(e) => setNewSub(t.topic_id, { name: e.target.value })}
                      className="px-2 py-1.5 rounded bg-[#171717] border border-[#262626] text-sm"/>
                    <input placeholder="slug"
                      data-testid={`admin-subtopic-new-slug-${t.topic_id}`}
                      value={(newSubByTopic[t.topic_id]?.slug) || ""}
                      onChange={(e) => setNewSub(t.topic_id, { slug: e.target.value })}
                      className="px-2 py-1.5 rounded bg-[#171717] border border-[#262626] font-mono text-sm"/>
                    <label className="text-xs inline-flex items-center gap-1">
                      <input type="checkbox"
                        checked={(newSubByTopic[t.topic_id]?.is_active) !== false}
                        onChange={(e) => setNewSub(t.topic_id, { is_active: e.target.checked })}/>
                      Aktif
                    </label>
                    <button data-testid={`admin-subtopic-add-${t.topic_id}`}
                      onClick={() => addSub(t.topic_id)}
                      className="inline-flex items-center gap-1 px-3 py-1.5 rounded bg-[#EA580C] hover:bg-[#F97316] text-white text-sm">
                      <Plus size={14}/> Alt konu ekle
                    </button>
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
}
